/* eslint-disable react/jsx-indent */

/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { getComponentFromName } from '../../lib/utils';

import ArrowButton from '../buttons/Arrow';
import * as Cards from '../cards';

import styles from '../../../styles/partials/slider.module.css';

const propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({})),
    size: PropTypes.string,
    placeholder: PropTypes.bool,
    cardType: PropTypes.string,
    cardProps: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    withoutArrows: PropTypes.bool,
    className: PropTypes.string,
};

function Slider({
    items = [],
    size = null,
    placeholder = false,
    cardType = 'film',
    cardProps = null,
    withoutArrows = false,
    className = null,
}) {
    const Card = getComponentFromName(cardType, Cards);

    const containerRef = useRef(null);
    const scrollRef = useRef(null);

    const [scrollPosition, setScrollPosition] = useState(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [scrollWidth, setScrollWidth] = useState(0);

    useEffect(() => {
        const { width: containerRefWidth = 0 } = containerRef.current.getBoundingClientRect() || {};
        const { scrollWidth: scrollRefWidth = 0 } = scrollRef.current || {};
        setContainerWidth(containerRefWidth);
        setScrollWidth(scrollRefWidth);
    }, [setContainerWidth, setScrollWidth, containerRef.current, scrollRef.current]);

    const onScrollLeft = useCallback(() => {
        if (scrollRef.current !== null) {
            const { scrollLeft = 0 } = scrollRef.current || {};
            scrollRef.current.scrollLeft = Math.max(scrollLeft - 320, 0);
        }
    }, []);

    const onScrollRight = useCallback(() => {
        if (scrollRef.current !== null) {
            const { scrollWidth: scrollRefWidth = 0, clientWidth = 0 } = scrollRef.current || {};
            const maxScrollLeft = scrollRefWidth - clientWidth;

            scrollRef.current.scrollLeft = Math.min(
                scrollRef.current.scrollLeft + 320,
                maxScrollLeft,
            );
        }
    }, []);

    const onScrollChange = useCallback(
        (e) => {
            const { target = null } = e || {};
            const {
                scrollLeft = 0,
                scrollWidth: scrollRefWidth = 0,
                clientWidth = 0,
            } = target || {};

            const maxScrollLeft = scrollRefWidth - clientWidth;

            const isLeft = scrollLeft <= 1;
            const isRight = scrollLeft >= maxScrollLeft - 20;

            if (scrollPosition !== 'left' && isLeft) {
                setScrollPosition('left');
            } else if (scrollPosition !== 'right' && isRight) {
                setScrollPosition('right');
            } else if (scrollPosition !== 'center' && !isLeft && !isRight) {
                setScrollPosition('center');
            }
        },
        [scrollPosition, setScrollPosition],
    );

    useEffect(() => {
        const { scrollLeft = 0 } = scrollRef.current || {};
        if (scrollLeft === 0 && scrollWidth > containerWidth + 20) {
            setScrollPosition('left');
        }
    }, [containerWidth, scrollWidth]);

    useEffect(() => {
        if (scrollRef.current !== null) {
            scrollRef.current.addEventListener('scroll', onScrollChange);
        }
        return () => {
            if (scrollRef.current !== null) {
                scrollRef.current.removeEventListener('scroll', onScrollChange);
            }
        };
    }, [onScrollChange]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles[size]]: size !== null,
                    [className]: className !== null,
                    [styles[cardType]]: cardType !== null,
                },
            ])}
            ref={containerRef}
        >
            <div className={styles.items} ref={scrollRef}>
                {(items || []).map((item, i) => (
                    <Card
                        key={`slider-card-${i + 1}-${item?.id}`}
                        item={item}
                        className={styles.item}
                        {...cardProps}
                    />
                ))}
                {placeholder
                    ? Array.from({ length: 3 }).map((_, i) => (
                          <Card key={`slider-place-${i + 1}`} className={styles.item} placeholder />
                      ))
                    : null}
            </div>
            {items !== null && items.length > 1 && !withoutArrows ? (
                <div className={styles.navigationButtons}>
                    <ArrowButton
                        className={classNames([
                            styles.prevButton,
                            {
                                [styles.hidden]:
                                    scrollPosition === null || scrollPosition === 'left',
                            },
                        ])}
                        direction="left"
                        color="#E8E2DE"
                        short
                        disabled={scrollPosition === 'left'}
                        onClick={onScrollLeft}
                    />
                    <ArrowButton
                        className={classNames([
                            styles.nextButton,
                            {
                                [styles.hidden]:
                                    scrollPosition === null || scrollPosition === 'right',
                            },
                        ])}
                        color="#E8E2DE"
                        short
                        disabled={scrollPosition === 'right'}
                        onClick={onScrollRight}
                    />
                </div>
            ) : null}
        </div>
    );
}

Slider.propTypes = propTypes;

export default Slider;
